import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  Package, Plus, Trash2, Search, Pencil, Barcode, 
  AlertTriangle, TrendingDown, Layers, Tags, 
  Warehouse, ArrowUpDown, Filter, Download,
  ScanLine, Box, Archive
} from 'lucide-react';
import { formatCurrency } from '../utils/exportHelpers';

export default function Inventory() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  
  // State
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('products');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStock, setFilterStock] = useState('');
  
  // Dialog states
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [barcodeInput, setBarcodeInput] = useState('');
  const barcodeInputRef = useRef(null);
  
  // New product form
  const [newProduct, setNewProduct] = useState({
    product_code: '',
    barcode: '',
    product_name_ar: '',
    product_name_en: '',
    category_code: '',
    description: '',
    base_unit: 'piece',
    units: [],
    purchase_price: 0,
    sale_price: 0,
    min_sale_price: 0,
    current_stock: 0,
    min_stock_level: 0,
    max_stock_level: 0,
    reorder_level: 0,
    is_taxable: true,
    vat_rate: 15,
    sales_account_code: '41100',
    purchase_account_code: '51200',
    inventory_account_code: '11310',
    is_active: true,
    is_service: false,
    track_inventory: true
  });
  
  // New category form
  const [newCategory, setNewCategory] = useState({
    category_code: '',
    category_name_ar: '',
    category_name_en: '',
    description: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [productsRes, categoriesRes, unitsRes, statsRes] = await Promise.all([
        api.get('/products'),
        api.get('/categories'),
        api.get('/units'),
        api.get('/inventory/stats')
      ]);
      setProducts(productsRes.data);
      setCategories(categoriesRes.data);
      setUnits(unitsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('حدث خطأ في تحميل البيانات');
    } finally {
      setLoading(false);
    }
  };

  const loadNextProductCode = async () => {
    try {
      const res = await api.get('/products/next-code');
      setNewProduct(prev => ({ ...prev, product_code: res.data.next_code }));
    } catch (error) {
      console.error('Error loading next code');
    }
  };

  // Barcode scanner handler
  const handleBarcodeSearch = async (barcode) => {
    if (!barcode) return;
    try {
      const res = await api.get(`/products/barcode/${barcode}`);
      toast.success(`تم العثور على: ${res.data.product.product_name_ar}`);
      // Could auto-add to invoice or show product details
    } catch (error) {
      toast.error('لم يتم العثور على منتج بهذا الباركود');
    }
    setBarcodeInput('');
  };

  const handleBarcodeKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleBarcodeSearch(barcodeInput);
    }
  };

  // Product CRUD
  const handleSaveProduct = async (e) => {
    e.preventDefault();
    
    if (!newProduct.product_code || !newProduct.product_name_ar) {
      toast.error('يرجى ملء الحقول المطلوبة');
      return;
    }
    
    try {
      if (editMode && editingProduct) {
        await api.put(`/products/${editingProduct}`, newProduct);
        toast.success('تم تحديث المنتج بنجاح');
      } else {
        await api.post('/products', newProduct);
        toast.success('تم إضافة المنتج بنجاح');
      }
      setProductDialogOpen(false);
      resetProductForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'حدث خطأ');
    }
  };

  const editProduct = (product) => {
    setNewProduct({
      ...product,
      units: product.units || []
    });
    setEditMode(true);
    setEditingProduct(product.product_code);
    setProductDialogOpen(true);
  };

  const deleteProduct = async (productCode) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا المنتج؟')) return;
    try {
      await api.delete(`/products/${productCode}`);
      toast.success('تم حذف المنتج بنجاح');
      loadData();
    } catch (error) {
      toast.error('حدث خطأ أثناء الحذف');
    }
  };

  const resetProductForm = () => {
    setNewProduct({
      product_code: '',
      barcode: '',
      product_name_ar: '',
      product_name_en: '',
      category_code: '',
      description: '',
      base_unit: 'piece',
      units: [],
      purchase_price: 0,
      sale_price: 0,
      min_sale_price: 0,
      current_stock: 0,
      min_stock_level: 0,
      max_stock_level: 0,
      reorder_level: 0,
      is_taxable: true,
      vat_rate: 15,
      sales_account_code: '41100',
      purchase_account_code: '51200',
      inventory_account_code: '11310',
      is_active: true,
      is_service: false,
      track_inventory: true
    });
    setEditMode(false);
    setEditingProduct(null);
  };

  // Category CRUD
  const handleSaveCategory = async (e) => {
    e.preventDefault();
    try {
      await api.post('/categories', {
        ...newCategory,
        is_active: true
      });
      toast.success('تم إضافة الفئة بنجاح');
      setCategoryDialogOpen(false);
      setNewCategory({ category_code: '', category_name_ar: '', category_name_en: '', description: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'حدث خطأ');
    }
  };

  const deleteCategory = async (categoryCode) => {
    if (!window.confirm('هل أنت متأكد من حذف هذه الفئة؟')) return;
    try {
      await api.delete(`/categories/${categoryCode}`);
      toast.success('تم حذف الفئة بنجاح');
      loadData();
    } catch (error) {
      toast.error('حدث خطأ أثناء الحذف');
    }
  };

  // Add unit to product
  const addProductUnit = () => {
    setNewProduct(prev => ({
      ...prev,
      units: [...prev.units, {
        unit_code: '',
        unit_name_ar: '',
        unit_name_en: '',
        conversion_factor: 1,
        barcode: '',
        sale_price: 0,
        purchase_price: 0
      }]
    }));
  };

  const removeProductUnit = (index) => {
    setNewProduct(prev => ({
      ...prev,
      units: prev.units.filter((_, i) => i !== index)
    }));
  };

  const updateProductUnit = (index, field, value) => {
    setNewProduct(prev => ({
      ...prev,
      units: prev.units.map((unit, i) => 
        i === index ? { ...unit, [field]: value } : unit
      )
    }));
  };

  // Filtering
  const filteredProducts = products.filter(product => {
    const matchesSearch = !searchTerm || 
      product.product_name_ar?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.product_name_en?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.product_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.barcode?.includes(searchTerm);
    
    const matchesCategory = !filterCategory || product.category_code === filterCategory;
    
    let matchesStock = true;
    if (filterStock === 'low') {
      matchesStock = product.current_stock < product.min_stock_level && product.min_stock_level > 0;
    } else if (filterStock === 'out') {
      matchesStock = product.current_stock <= 0;
    }
    
    return matchesSearch && matchesCategory && matchesStock;
  });

  const getUnitName = (unitCode) => {
    const unit = units.find(u => u.unit_code === unitCode);
    return unit?.unit_name_ar || unitCode;
  };

  const getCategoryName = (categoryCode) => {
    const category = categories.find(c => c.category_code === categoryCode);
    return category?.category_name_ar || '-';
  };

  if (loading) return <div className="text-center py-12">جاري التحميل...</div>;

  return (
    <div className="space-y-6" data-testid="inventory-container">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Package className="h-7 w-7 text-emerald" />
            إدارة المخزون
          </h1>
          <p className="text-muted-foreground text-sm mt-1">إدارة الأصناف والمنتجات والمخزون</p>
        </div>
        <div className="flex gap-2">
          <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="add-category-button">
                <Tags className="h-4 w-4 me-2" />فئة جديدة
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>إضافة فئة جديدة</DialogTitle>
                <DialogDescription>أضف فئة لتصنيف المنتجات</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSaveCategory} className="space-y-4">
                <div>
                  <Label>كود الفئة *</Label>
                  <Input 
                    value={newCategory.category_code}
                    onChange={(e) => setNewCategory({...newCategory, category_code: e.target.value})}
                    placeholder="مثال: CAT001"
                    required
                  />
                </div>
                <div>
                  <Label>اسم الفئة بالعربية *</Label>
                  <Input 
                    value={newCategory.category_name_ar}
                    onChange={(e) => setNewCategory({...newCategory, category_name_ar: e.target.value})}
                    placeholder="مثال: إلكترونيات"
                    required
                  />
                </div>
                <div>
                  <Label>اسم الفئة بالإنجليزية</Label>
                  <Input 
                    value={newCategory.category_name_en}
                    onChange={(e) => setNewCategory({...newCategory, category_name_en: e.target.value})}
                    placeholder="e.g., Electronics"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setCategoryDialogOpen(false)}>إلغاء</Button>
                  <Button type="submit" className="bg-emerald hover:bg-emerald-hover">حفظ</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          
          <Dialog open={productDialogOpen} onOpenChange={(open) => {
            setProductDialogOpen(open);
            if (open && !editMode) {
              loadNextProductCode();
            }
          }}>
            <DialogTrigger asChild>
              <Button className="bg-emerald hover:bg-emerald-hover" onClick={resetProductForm} data-testid="add-product-button">
                <Plus className="h-4 w-4 me-2" />إضافة صنف
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editMode ? 'تعديل صنف' : 'إضافة صنف جديد'}</DialogTitle>
                <DialogDescription>أضف منتج أو صنف جديد للمخزون</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSaveProduct} className="space-y-6">
                {/* Basic Info */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label>كود الصنف *</Label>
                    <Input 
                      value={newProduct.product_code}
                      onChange={(e) => setNewProduct({...newProduct, product_code: e.target.value})}
                      placeholder="P00001"
                      required
                      disabled={editMode}
                      data-testid="product-code-input"
                    />
                  </div>
                  <div>
                    <Label>الباركود</Label>
                    <div className="relative">
                      <Input 
                        value={newProduct.barcode}
                        onChange={(e) => setNewProduct({...newProduct, barcode: e.target.value})}
                        placeholder="123456789"
                        className="ps-10"
                        data-testid="product-barcode-input"
                      />
                      <Barcode className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    </div>
                  </div>
                  <div>
                    <Label>الفئة</Label>
                    <select
                      value={newProduct.category_code}
                      onChange={(e) => setNewProduct({...newProduct, category_code: e.target.value})}
                      className="w-full h-10 px-3 border rounded-md bg-background"
                    >
                      <option value="">-- اختر الفئة --</option>
                      {categories.map(cat => (
                        <option key={cat.category_code} value={cat.category_code}>
                          {cat.category_name_ar}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>اسم الصنف بالعربية *</Label>
                    <Input 
                      value={newProduct.product_name_ar}
                      onChange={(e) => setNewProduct({...newProduct, product_name_ar: e.target.value})}
                      placeholder="اسم المنتج"
                      required
                      data-testid="product-name-ar-input"
                    />
                  </div>
                  <div>
                    <Label>اسم الصنف بالإنجليزية</Label>
                    <Input 
                      value={newProduct.product_name_en}
                      onChange={(e) => setNewProduct({...newProduct, product_name_en: e.target.value})}
                      placeholder="Product Name"
                    />
                  </div>
                </div>

                {/* Unit & Pricing */}
                <div className="border-t pt-4">
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <Box className="h-4 w-4" />
                    الوحدات والأسعار
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <Label>الوحدة الأساسية</Label>
                      <select
                        value={newProduct.base_unit}
                        onChange={(e) => setNewProduct({...newProduct, base_unit: e.target.value})}
                        className="w-full h-10 px-3 border rounded-md bg-background"
                      >
                        {units.map(unit => (
                          <option key={unit.unit_code} value={unit.unit_code}>
                            {unit.unit_name_ar}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label>سعر الشراء</Label>
                      <Input 
                        type="number"
                        step="0.01"
                        value={newProduct.purchase_price}
                        onChange={(e) => setNewProduct({...newProduct, purchase_price: parseFloat(e.target.value) || 0})}
                        className="text-end"
                      />
                    </div>
                    <div>
                      <Label>سعر البيع</Label>
                      <Input 
                        type="number"
                        step="0.01"
                        value={newProduct.sale_price}
                        onChange={(e) => setNewProduct({...newProduct, sale_price: parseFloat(e.target.value) || 0})}
                        className="text-end"
                        data-testid="product-sale-price-input"
                      />
                    </div>
                    <div>
                      <Label>أقل سعر بيع</Label>
                      <Input 
                        type="number"
                        step="0.01"
                        value={newProduct.min_sale_price}
                        onChange={(e) => setNewProduct({...newProduct, min_sale_price: parseFloat(e.target.value) || 0})}
                        className="text-end"
                      />
                    </div>
                  </div>
                </div>

                {/* Additional Units */}
                <div className="border-t pt-4">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="font-semibold flex items-center gap-2">
                      <Layers className="h-4 w-4" />
                      وحدات إضافية (كرتون، درزن، باكج...)
                    </h3>
                    <Button type="button" variant="outline" size="sm" onClick={addProductUnit}>
                      <Plus className="h-4 w-4 me-1" />إضافة وحدة
                    </Button>
                  </div>
                  
                  {newProduct.units.length > 0 && (
                    <div className="space-y-3">
                      {newProduct.units.map((unit, index) => (
                        <div key={index} className="grid grid-cols-12 gap-2 p-3 border rounded-lg bg-muted/30">
                          <div className="col-span-2">
                            <Label className="text-xs">الوحدة</Label>
                            <select
                              value={unit.unit_code}
                              onChange={(e) => {
                                const selectedUnit = units.find(u => u.unit_code === e.target.value);
                                updateProductUnit(index, 'unit_code', e.target.value);
                                if (selectedUnit) {
                                  updateProductUnit(index, 'unit_name_ar', selectedUnit.unit_name_ar);
                                  updateProductUnit(index, 'unit_name_en', selectedUnit.unit_name_en || '');
                                }
                              }}
                              className="w-full h-9 px-2 border rounded-md bg-background text-sm"
                            >
                              <option value="">اختر</option>
                              {units.map(u => (
                                <option key={u.unit_code} value={u.unit_code}>{u.unit_name_ar}</option>
                              ))}
                            </select>
                          </div>
                          <div className="col-span-2">
                            <Label className="text-xs">عامل التحويل</Label>
                            <Input 
                              type="number"
                              step="0.01"
                              value={unit.conversion_factor}
                              onChange={(e) => updateProductUnit(index, 'conversion_factor', parseFloat(e.target.value) || 1)}
                              className="text-end h-9"
                              placeholder="12"
                            />
                          </div>
                          <div className="col-span-2">
                            <Label className="text-xs">الباركود</Label>
                            <Input 
                              value={unit.barcode || ''}
                              onChange={(e) => updateProductUnit(index, 'barcode', e.target.value)}
                              className="h-9"
                              placeholder="باركود الوحدة"
                            />
                          </div>
                          <div className="col-span-2">
                            <Label className="text-xs">سعر الشراء</Label>
                            <Input 
                              type="number"
                              step="0.01"
                              value={unit.purchase_price}
                              onChange={(e) => updateProductUnit(index, 'purchase_price', parseFloat(e.target.value) || 0)}
                              className="text-end h-9"
                            />
                          </div>
                          <div className="col-span-2">
                            <Label className="text-xs">سعر البيع</Label>
                            <Input 
                              type="number"
                              step="0.01"
                              value={unit.sale_price}
                              onChange={(e) => updateProductUnit(index, 'sale_price', parseFloat(e.target.value) || 0)}
                              className="text-end h-9"
                            />
                          </div>
                          <div className="col-span-2 flex items-end">
                            <Button type="button" variant="ghost" size="icon" onClick={() => removeProductUnit(index)}>
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </div>
                      ))}
                      <p className="text-xs text-muted-foreground">
                        * عامل التحويل: مثلاً إذا كان الكرتون يحتوي 12 قطعة، ضع 12
                      </p>
                    </div>
                  )}
                </div>

                {/* Stock Management */}
                <div className="border-t pt-4">
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <Archive className="h-4 w-4" />
                    إدارة المخزون
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <Label>المخزون الحالي</Label>
                      <Input 
                        type="number"
                        step="0.01"
                        value={newProduct.current_stock}
                        onChange={(e) => setNewProduct({...newProduct, current_stock: parseFloat(e.target.value) || 0})}
                        className="text-end"
                        data-testid="product-stock-input"
                      />
                    </div>
                    <div>
                      <Label>الحد الأدنى</Label>
                      <Input 
                        type="number"
                        step="0.01"
                        value={newProduct.min_stock_level}
                        onChange={(e) => setNewProduct({...newProduct, min_stock_level: parseFloat(e.target.value) || 0})}
                        className="text-end"
                      />
                    </div>
                    <div>
                      <Label>الحد الأقصى</Label>
                      <Input 
                        type="number"
                        step="0.01"
                        value={newProduct.max_stock_level}
                        onChange={(e) => setNewProduct({...newProduct, max_stock_level: parseFloat(e.target.value) || 0})}
                        className="text-end"
                      />
                    </div>
                    <div>
                      <Label>مستوى إعادة الطلب</Label>
                      <Input 
                        type="number"
                        step="0.01"
                        value={newProduct.reorder_level}
                        onChange={(e) => setNewProduct({...newProduct, reorder_level: parseFloat(e.target.value) || 0})}
                        className="text-end"
                      />
                    </div>
                  </div>
                  <div className="flex gap-6 mt-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={newProduct.track_inventory}
                        onChange={(e) => setNewProduct({...newProduct, track_inventory: e.target.checked})}
                        className="rounded"
                      />
                      <span className="text-sm">تتبع المخزون</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={newProduct.is_taxable}
                        onChange={(e) => setNewProduct({...newProduct, is_taxable: e.target.checked})}
                        className="rounded"
                      />
                      <span className="text-sm">خاضع للضريبة ({newProduct.vat_rate}%)</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={newProduct.is_service}
                        onChange={(e) => setNewProduct({...newProduct, is_service: e.target.checked})}
                        className="rounded"
                      />
                      <span className="text-sm">خدمة (ليس منتج)</span>
                    </label>
                  </div>
                </div>

                {/* Description */}
                <div>
                  <Label>الوصف</Label>
                  <textarea
                    value={newProduct.description}
                    onChange={(e) => setNewProduct({...newProduct, description: e.target.value})}
                    className="w-full h-20 px-3 py-2 border rounded-md bg-background resize-none"
                    placeholder="وصف المنتج..."
                  />
                </div>

                <div className="flex justify-end gap-2 pt-4 border-t">
                  <Button type="button" variant="outline" onClick={() => setProductDialogOpen(false)}>إلغاء</Button>
                  <Button type="submit" className="bg-emerald hover:bg-emerald-hover" data-testid="save-product-button">
                    {editMode ? 'تحديث' : 'حفظ'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald/10 rounded-lg">
                <Package className="h-5 w-5 text-emerald" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">إجمالي الأصناف</p>
                <p className="text-xl font-bold">{stats.total_products}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/10 rounded-lg">
                <Tags className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">الفئات</p>
                <p className="text-xl font-bold">{stats.total_categories}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-500/10 rounded-lg">
                <TrendingDown className="h-5 w-5 text-yellow-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">مخزون منخفض</p>
                <p className="text-xl font-bold text-yellow-500">{stats.low_stock_count}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-500/10 rounded-lg">
                <AlertTriangle className="h-5 w-5 text-red-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">نفاد المخزون</p>
                <p className="text-xl font-bold text-red-500">{stats.out_of_stock_count}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 col-span-2">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-500/10 rounded-lg">
                <Warehouse className="h-5 w-5 text-purple-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">قيمة المخزون</p>
                <p className="text-xl font-bold text-purple-500">{formatCurrency(stats.total_stock_value)}</p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Barcode Scanner Input */}
      <Card className="p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <ScanLine className="absolute start-3 top-1/2 -translate-y-1/2 h-5 w-5 text-emerald" />
            <Input
              ref={barcodeInputRef}
              value={barcodeInput}
              onChange={(e) => setBarcodeInput(e.target.value)}
              onKeyPress={handleBarcodeKeyPress}
              placeholder="امسح الباركود أو أدخله يدوياً ثم اضغط Enter..."
              className="ps-10 text-lg h-12"
              data-testid="barcode-scanner-input"
            />
          </div>
          <Button 
            onClick={() => handleBarcodeSearch(barcodeInput)}
            className="bg-emerald hover:bg-emerald-hover h-12"
          >
            <Search className="h-4 w-4 me-2" />
            بحث بالباركود
          </Button>
        </div>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2 max-w-md">
          <TabsTrigger value="products" className="flex items-center gap-2">
            <Package className="h-4 w-4" />
            الأصناف ({products.length})
          </TabsTrigger>
          <TabsTrigger value="categories" className="flex items-center gap-2">
            <Tags className="h-4 w-4" />
            الفئات ({categories.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="products" className="mt-4">
          {/* Filters */}
          <Card className="p-4 mb-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="بحث بالاسم أو الكود أو الباركود..."
                  className="ps-10"
                  data-testid="search-products-input"
                />
              </div>
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="h-10 px-3 border rounded-md bg-background min-w-[150px]"
              >
                <option value="">كل الفئات</option>
                {categories.map(cat => (
                  <option key={cat.category_code} value={cat.category_code}>
                    {cat.category_name_ar}
                  </option>
                ))}
              </select>
              <select
                value={filterStock}
                onChange={(e) => setFilterStock(e.target.value)}
                className="h-10 px-3 border rounded-md bg-background min-w-[150px]"
              >
                <option value="">كل المخزون</option>
                <option value="low">مخزون منخفض</option>
                <option value="out">نفاد المخزون</option>
              </select>
            </div>
          </Card>

          {/* Products Table */}
          <Card className="border">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="products-table">
                <thead className="bg-muted">
                  <tr>
                    <th className="px-4 py-3 text-start">الكود</th>
                    <th className="px-4 py-3 text-start">الباركود</th>
                    <th className="px-4 py-3 text-start">اسم الصنف</th>
                    <th className="px-4 py-3 text-start">الفئة</th>
                    <th className="px-4 py-3 text-start">الوحدة</th>
                    <th className="px-4 py-3 text-end">سعر البيع</th>
                    <th className="px-4 py-3 text-end">المخزون</th>
                    <th className="px-4 py-3 text-start">الحالة</th>
                    <th className="px-4 py-3 text-start">الإجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProducts.map((product) => {
                    const isLowStock = product.current_stock < product.min_stock_level && product.min_stock_level > 0;
                    const isOutOfStock = product.current_stock <= 0;
                    
                    return (
                      <tr key={product.product_code} className="border-b hover:bg-muted/30" data-testid={`product-row-${product.product_code}`}>
                        <td className="px-4 py-3 font-mono text-sm">{product.product_code}</td>
                        <td className="px-4 py-3 font-mono text-sm text-muted-foreground">{product.barcode || '-'}</td>
                        <td className="px-4 py-3">
                          <div>
                            <p className="font-medium">{product.product_name_ar}</p>
                            {product.product_name_en && (
                              <p className="text-xs text-muted-foreground">{product.product_name_en}</p>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm">{getCategoryName(product.category_code)}</td>
                        <td className="px-4 py-3 text-sm">{getUnitName(product.base_unit)}</td>
                        <td className="px-4 py-3 text-end font-medium">{formatCurrency(product.sale_price)}</td>
                        <td className="px-4 py-3 text-end">
                          <span className={`font-medium ${isOutOfStock ? 'text-red-500' : isLowStock ? 'text-yellow-500' : ''}`}>
                            {product.current_stock}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {isOutOfStock ? (
                            <Badge variant="destructive" className="text-xs">نفاد</Badge>
                          ) : isLowStock ? (
                            <Badge variant="warning" className="bg-yellow-500/20 text-yellow-600 text-xs">منخفض</Badge>
                          ) : product.is_active ? (
                            <Badge variant="outline" className="bg-emerald/10 text-emerald text-xs">متوفر</Badge>
                          ) : (
                            <Badge variant="secondary" className="text-xs">غير نشط</Badge>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-1">
                            <Button size="icon" variant="ghost" onClick={() => editProduct(product)} data-testid={`edit-product-${product.product_code}`}>
                              <Pencil className="h-4 w-4 text-blue-500" />
                            </Button>
                            <Button size="icon" variant="ghost" onClick={() => deleteProduct(product.product_code)} data-testid={`delete-product-${product.product_code}`}>
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {filteredProducts.length === 0 && (
                <div className="text-center py-12 text-muted-foreground">
                  <Package className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>لا توجد أصناف</p>
                  <p className="text-sm">أضف أصناف جديدة للبدء</p>
                </div>
              )}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="categories" className="mt-4">
          <Card className="border">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="categories-table">
                <thead className="bg-muted">
                  <tr>
                    <th className="px-4 py-3 text-start">الكود</th>
                    <th className="px-4 py-3 text-start">اسم الفئة</th>
                    <th className="px-4 py-3 text-start">الاسم بالإنجليزية</th>
                    <th className="px-4 py-3 text-start">الوصف</th>
                    <th className="px-4 py-3 text-start">الإجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  {categories.map((category) => (
                    <tr key={category.category_code} className="border-b hover:bg-muted/30">
                      <td className="px-4 py-3 font-mono">{category.category_code}</td>
                      <td className="px-4 py-3 font-medium">{category.category_name_ar}</td>
                      <td className="px-4 py-3 text-muted-foreground">{category.category_name_en || '-'}</td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">{category.description || '-'}</td>
                      <td className="px-4 py-3">
                        <Button size="icon" variant="ghost" onClick={() => deleteCategory(category.category_code)}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {categories.length === 0 && (
                <div className="text-center py-12 text-muted-foreground">
                  <Tags className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>لا توجد فئات</p>
                  <p className="text-sm">أضف فئات لتنظيم منتجاتك</p>
                </div>
              )}
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
